import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import './App.css';

import theme from './theme';
import {
  bundeslaenderListe,
  findeVerteilungHa,
  landkreiseFuerBundesland,
  loadBundeslaender,
  loadFelderForLandkreis,
  loadLandkreise,
  loadRegionInfo,
  RegionCollection,
  RegionInfoMap,
  RegionListItem,
} from './regions';
import { Verteilung } from './klassen';
import Masthead from './components/Masthead';
import RegionSidebar from './components/RegionSidebar';
import MapView, { ZOOM_LANDKREIS } from './components/MapView';
import DonutLegend from './components/DonutLegend';
import InfoPanel from './components/InfoPanel';

export type Level = 'bundesland' | 'landkreis' | 'feld';

export type Selection = {
  level: Level;
  id: string;
  name: string;
  /** Anteile der gewählten Region (falls beim Auswählen bekannt). */
  verteilung?: Verteilung;
  /** Klassenverteilung in Hektar (aus der Datenquelle). */
  verteilungHa?: Verteilung;
  /** Fläche der Region in Hektar. */
  sizeHa?: number;
  /** Fremdschlüssel für die Sidebar-Kopplung / Nachladen von Feldern. */
  parentBundeslandId?: string;
  parentLandkreisId?: string;
} | null;

const EMPTY_COLLECTION: RegionCollection = { type: 'FeatureCollection', features: [] };

const App: React.FC = () => {
  const [bundeslaender, setBundeslaender] = useState<RegionCollection>(EMPTY_COLLECTION);
  const [landkreise, setLandkreise] = useState<RegionCollection>(EMPTY_COLLECTION);
  const [regionInfo, setRegionInfo] = useState<RegionInfoMap>({});
  const [selection, setSelection] = useState<Selection>(null);
  const [zoom, setZoom] = useState<number>(0);
  const [ladeFehler, setLadeFehler] = useState<boolean>(false);

  // Grenzen + Info einmalig laden (Felder bedarfsgerecht in MapView).
  useEffect(() => {
    let aktiv = true;
    Promise.all([loadBundeslaender(), loadLandkreise(), loadRegionInfo()]).then(
      ([bl, lk, info]) => {
        if (!aktiv) return;
        setBundeslaender(bl);
        setLandkreise(lk);
        setRegionInfo(info);
        // Leere Grenzdaten → Laden fehlgeschlagen (Netzfehler/404): Hinweis zeigen.
        setLadeFehler(bl.features.length === 0);
      }
    );
    return () => {
      aktiv = false;
    };
  }, []);

  const blListe: RegionListItem[] = useMemo(
    () => bundeslaenderListe(bundeslaender),
    [bundeslaender]
  );

  const getLandkreise = useCallback(
    (bundeslandId: string) => landkreiseFuerBundesland(landkreise, bundeslandId),
    [landkreise]
  );

  // Synthetische Auswahl für das gesamte Bundesland (erstes Feature = RP).
  const bundeslandSelection: Selection = useMemo(() => {
    const f = bundeslaender.features[0];
    if (!f) return null;
    return {
      level: 'bundesland',
      id: f.properties.id,
      name: f.properties.name ?? f.properties.id,
      verteilung: f.properties.verteilung,
      verteilungHa: f.properties.verteilungHa,
      sizeHa: f.properties.flaecheHa,
    };
  }, [bundeslaender]);

  // Übersicht unten rechts (§ Anforderung 4): komplett herausgezoomt ODER ohne
  // explizit gewählten Landkreis/Feld → ganzes Bundesland; sonst die Auswahl.
  const overview: Selection = useMemo(() => {
    const zeigeBundesland =
      zoom < ZOOM_LANDKREIS || !selection || selection.level === 'bundesland';
    return zeigeBundesland ? bundeslandSelection : selection;
  }, [zoom, selection, bundeslandSelection]);

  // ha-Verteilung der Übersicht (Info-Panel zeigt Hektar): bevorzugt aus dem
  // Selection-Objekt, sonst aus den geladenen Collections (BL/LK).
  const aktuelleVerteilungHa: Verteilung | undefined = useMemo(() => {
    if (!overview) return undefined;
    return overview.verteilungHa ?? findeVerteilungHa(overview.id, bundeslaender, landkreise);
  }, [overview, bundeslaender, landkreise]);

  const info = overview ? regionInfo[overview.id] : undefined;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="app-shell">
        <Masthead />
        {ladeFehler ? (
          <Box className="data-error" role="alert">
            Daten konnten nicht geladen werden. Bitte die Seite neu laden.
          </Box>
        ) : null}
        <Box className="region-shell">
          <Box className="sidebar-column">
            <RegionSidebar
              bundeslaender={blListe}
              getLandkreise={getLandkreise}
              selection={selection}
              onSelect={setSelection}
            />
            <InfoPanel selection={overview} verteilungHa={aktuelleVerteilungHa} info={info} />
          </Box>
          <Box className="map-column">
            <Box className="map-area">
              <MapView
                bundeslaender={bundeslaender}
                landkreise={landkreise}
                loadFelder={loadFelderForLandkreis}
                selection={selection}
                onSelect={setSelection}
                onZoomChange={setZoom}
              />
              <DonutLegend />
            </Box>
          </Box>
        </Box>
        <Box component="footer" className="app-footer">
          <a
            href="https://www.flaticon.com/free-icons/maize"
            title="Maize icons"
            target="_blank"
            rel="noopener noreferrer"
          >
            Maize icons created by Design View - Flaticon
          </a>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default App;
